import { useMemo } from 'react';
import { useChatData } from '../context/ChatContext';

export function useConversations() {
  const {
    conversations,
    messages,
    searchQuery,
    conversationFilter,
    isSyncing,
    unreadCounts,
    syncConversations,
    dispatch,
  } = useChatData();

  const filteredConversations = useMemo(() => {
    let result = conversations;

    // Filter by type
    if (conversationFilter === 'dms') {
      result = result.filter((c) => !c.isGroup);
    } else if (conversationFilter === 'groups') {
      result = result.filter((c) => c.isGroup);
    }

    // Filter by search query — matches wallet addresses, group names, member addresses, and message content
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((c) => {
        // Match conversation-level fields
        if (c.peerAddress.toLowerCase().includes(query)) return true;
        if (c.groupName?.toLowerCase().includes(query)) return true;
        if (c.lastMessageText?.toLowerCase().includes(query)) return true;

        // Match group member addresses
        if (c.members?.some((addr) => addr.toLowerCase().includes(query))) return true;

        // Match loaded message content and sender addresses
        const convoMessages = messages[c.id];
        if (convoMessages?.length) {
          return convoMessages.some(
            (m) =>
              (typeof m.content === 'string' && m.content.toLowerCase().includes(query)) ||
              m.senderAddress.toLowerCase().includes(query)
          );
        }

        return false;
      });
    }

    return result;
  }, [conversations, conversationFilter, searchQuery, messages]);

  const setSearchQuery = (query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  };

  const setFilter = (filter: 'all' | 'dms' | 'groups') => {
    dispatch({ type: 'SET_CONVERSATION_FILTER', payload: filter });
  };

  return {
    conversations: filteredConversations,
    allConversations: conversations,
    isSyncing,
    unreadCounts,
    searchQuery,
    conversationFilter,
    setSearchQuery,
    setFilter,
    syncConversations,
  };
}
